import { GroupedRoute } from "./types/transitTypes";

// Converts timestamp to Date object if needed
// Handles Google Maps time objects with numeric values
export const convertToDate = (timeObj: any): any => {
  if (timeObj && typeof timeObj === "object") {
    const newTimeObj = { ...timeObj };
    if (typeof timeObj.value === "number") {
      newTimeObj.value = new Date(timeObj.value);
    }
    return newTimeObj;
  }
  return timeObj;
};

// Formats distance for display, returning a fallback if unavailable
export const formatDistance = (distance: any): string => {
  return distance?.text || "unknown distance";
};

// Formats duration for display, returning a fallback if unavailable
export const formatDuration = (duration: any): string => {
  return duration?.text || "unknown duration";
};

// Calculates total route duration in hours and minutes
export const calculateTotalDuration = (route: GroupedRoute): string => {
  if (!route?.legs) return "unknown duration";

  let totalSeconds = 0;
  route.legs.forEach((leg: any) => {
    leg.steps.forEach((step: any) => {
      if (step.duration?.value) {
        totalSeconds += step.duration.value;
      }
    });
  });

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  let durationText = "";
  if (hours > 0) durationText += `${hours} hour${hours !== 1 ? "s" : ""}`;
  if (hours > 0 && minutes > 0) durationText += " ";
  if (minutes > 0) durationText += `${minutes} min${minutes !== 1 ? "s" : ""}`;
  return durationText || "0 mins";
};

// Calculates total route distance in miles
export const calculateTotalDistance = (route: GroupedRoute): string => {
  if (!route?.legs) return "unknown distance";

  let totalMeters = 0;
  route.legs.forEach((leg: any) => {
    leg.steps.forEach((step: any) => {
      if (step.distance?.value) {
        totalMeters += step.distance.value;
      }
    });
  });

  const totalMiles = totalMeters * 0.000621371;
  return `${totalMiles.toFixed(1)} mi`;
};

// Calculates text color based on background luminance for transit line styling
export const getContrastTextColor = (hexColor: string): string => {
  if (!hexColor) return "#FFFFFF";
  const hex = hexColor.replace("#", "");
  const r = parseInt(
    hex.length === 3 ? hex[0] + hex[0] : hex.substring(0, 2),
    16
  );
  const g = parseInt(hex.length === 3 ? hex[1] : hex.substring(2, 4), 16);
  const b = parseInt(hex.length === 3 ? hex[2] : hex.substring(4, 6), 16);
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance < 128 ? "#FFFFFF" : "#000000";
};

// Generates step-by-step route instructions as HTML strings
// Includes total duration, distance, fare, warnings, and copyrights
export const generateInstructions = (route: GroupedRoute): string[] => {
  const instructions: string[] = [];
  let stepIndex = 1;
  let previousDestination: string | null = null;

  if (!route?.legs) return instructions;

  // Add total route details
  const totalDuration = calculateTotalDuration(route);
  const totalDistance = calculateTotalDistance(route);
  instructions.push(
    `<b>Total Route Duration:</b> ${totalDuration}<br /><b>Total Distance:</b> ${totalDistance}`
  );
  // Add start address
  const startAddress = route.legs[0]?.start_address || "Unknown start address";
  instructions.push(`<b>Starting Location:</b> ${startAddress}`);

  // Add fare if available
  if (route.fare) {
    instructions.push(
      `<b>Fare:</b> ${route.fare.value.toFixed(2)} (${route.fare.currency})`
    );
  }

  // Replace direction abbreviations
  const replaceDirections = (text: string): string => {
    return text
      .replace(/\[eb\]/gi, "(eastbound)")
      .replace(/\[nb\]/gi, "(northbound)")
      .replace(/\[sb\]/gi, "(southbound)")
      .replace(/\[wb\]/gi, "(westbound)");
  };

  route.legs.forEach((leg: any, legIndex: number) => {
    leg.arrival_time = convertToDate(leg.arrival_time);
    leg.departure_time = convertToDate(leg.departure_time);

    leg.steps.forEach((step: any, stepIdx: number) => {
      let instructionText = "";
      const duration = formatDuration(step.duration);
      const distance = formatDistance(step.distance);
      const currentDestination =
        step.end_address || step.transit?.arrival_stop?.name || "destination";

      if (step.travel_mode === "TRANSIT" && step.transit) {
        step.transit.arrival_time = convertToDate(step.transit.arrival_time);
        step.transit.departure_time = convertToDate(
          step.transit.departure_time
        );

        const transit = step.transit;
        const vehicleType = transit.line?.vehicle?.type || "";
        const isBus = vehicleType === "BUS";
        const isTrain =
          vehicleType === "HEAVY_RAIL" ||
          vehicleType === "TRAM" ||
          vehicleType === "LIGHT_RAIL";
        if (isBus || isTrain) {
          const vehicle = isBus ? "Bus" : "Train";
          const shortName = isTrain
            ? transit.trip_short_name ||
              transit.line?.short_name ||
              transit.line?.name ||
              "Unknown"
            : transit.line?.short_name || transit.line?.name || "Unknown";

          const coloredVehicle = `${vehicle} ${shortName}`;
          const headsignRaw = transit.headsign || "";
          const headsignStartsWithTo = headsignRaw
            .toLowerCase()
            .startsWith("to ");
          const headsign = headsignStartsWithTo
            ? headsignRaw.substring(3)
            : headsignRaw;
          const headsignText = headsign ? ` to ${headsign}` : "";
          const arrivalStop = transit.arrival_stop?.name || "your destination";
          const iconUrl = transit.line?.vehicle?.icon || "";
          const bgColor = transit.line?.color || "#FFFFFF";
          const textColor = getContrastTextColor(bgColor);
          const agency = transit.line?.agencies?.[0]?.name || "Unknown agency";
          const agencyUrl = transit.line?.agencies?.[0]?.url || "";

          instructionText = `Take ${coloredVehicle}${headsignText}, arrive at ${arrivalStop}`;
          instructionText = instructionText.replace(/<[^>]+>/g, "");
          instructionText = replaceDirections(instructionText);

          const vehicleStyle = `style="color:${textColor};background-color:${bgColor};padding:2px 6px;border-radius:3px;"`;
          const iconHtml = iconUrl
            ? `<img src="${iconUrl}" alt="${vehicle.toLowerCase()} icon" style="width:16px;height:16px;vertical-align:middle;margin-right:5px;" />`
            : "";

          const timeOptions = route.timeOptions || [];
          const transitTimes = timeOptions.filter((time: any, idx: number) => {
            const stepIndexInTimes = Math.floor(
              idx /
                (route.legs.reduce(
                  (acc: number, leg: any) =>
                    acc +
                    leg.steps.filter((s: any) => s.travel_mode === "TRANSIT")
                      .length,
                  0
                ) /
                  timeOptions.length)
            );
            return stepIndexInTimes === stepIdx;
          });

          if (transitTimes.length > 1) {
            const timeOptionsHtml = transitTimes
              .map(
                (time: any) =>
                  `Depart: ${time.departureTime}, Arrive: ${time.arrivalTime}, Duration: ${time.duration}`
              )
              .join(" | ");
            instructions.push(
              `<hr>${iconHtml} ${instructionText.replace(
                coloredVehicle,
                `<span ${vehicleStyle}>${coloredVehicle}</span>`
              )}<br />Times: ${timeOptionsHtml}<br />Distance: ${distance}<br />Agency: ${agency} <a href="${agencyUrl}">(${agencyUrl})</a>`
            );
          } else {
            const departureTime =
              transit.departure_time?.text || "unknown time";
            const arrivalTime = transit.arrival_time?.text || "unknown time";
            instructions.push(
              `<hr>${iconHtml} ${instructionText.replace(
                coloredVehicle,
                `<span ${vehicleStyle}>${coloredVehicle}</span>`
              )}<br />Duration: ${duration}, Distance: ${distance}<br /><br />   Depart: ${departureTime}<br />   Arrive: ${arrivalTime}<br /><br />   Agency: ${agency} <a href="${agencyUrl}">(${agencyUrl})</a>`
            );
          }
        }
      } else if (step.travel_mode === "WALKING") {
        instructionText =
          step.instructions || `Walk to ${step.end_address || "destination"}`;
        instructionText = instructionText.replace(/<(div|wbr)[^>]*>/g, "");
        instructionText = replaceDirections(instructionText);
        // Use local bundled icon if available, fallback to remote
        const assetsUrl = (window as any).trpSettings?.assetsUrl || '';
        const iconSrc = assetsUrl 
          ? `${assetsUrl}/icons/walk.png`
          : 'https://maps.gstatic.com/mapfiles/transit/iw2/6/walk.png';
        const walkingIcon = `<img src="${iconSrc}" alt="walking icon" style="width:16px;height:16px;vertical-align:middle;margin-right:5px;" />`;

        instructions.push(
          `<hr>${walkingIcon} ${instructionText}<br />Duration: ${duration}, Distance: ${distance}`
        );

        if (step.steps && step.steps.length > 0) {
          step.steps.forEach((subStep: any, subStepIdx: number) => {
            let subInstruction = subStep.instructions || "Continue";
            subInstruction = replaceDirections(subInstruction);
            const subDistance = formatDistance(subStep.distance);
            const subDuration = formatDuration(subStep.duration);
            const subManeuver = subStep.maneuver
              ? ` (${subStep.maneuver.replace(/-/g, " ")})`
              : "";
            instructions.push(
              `<span style="display:block;margin-left:20px;margin-bottom:0;">${subInstruction}${subManeuver}<br />Distance: ${subDistance}, Duration: ${subDuration}</span>`
            );
          });
        }
      } else {
        instructionText = step.instructions || "Continue";
        instructionText = instructionText.replace(/<[^>]+>/g, "");
        instructionText = replaceDirections(instructionText);
        instructions.push(
          `${stepIndex}. ${instructionText}<br />Duration: ${duration}, Distance: ${distance}`
        );
      }

      previousDestination = currentDestination;
      stepIndex++;
    });
  });

  const endAddress =
    route.legs[route.legs.length - 1]?.end_address || "Unknown destination";
  instructions.push(`<b>Destination:</b> ${endAddress}`);

  if (route.warnings && route.warnings.length > 0) {
    instructions.push(`<hr><center>${route.warnings.join(", ")}</center>`);
  }
  if (route.copyrights) {
    instructions.push(`<center>${route.copyrights}</center>`);
  }

  return instructions;
};
